import { useCallback, useEffect, useMemo } from 'react';

import { useBxpWebStargateClient } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { FastshiftTicketStatus } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { checkFeatureFlagGate } from '@wac-web/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@wac-web/utils/feature-flags/feature-flag-manifest';

import { getOrgQueryParam } from '@/common/utils/get-org-query-param';
import {
	useFastshiftDispatch,
	useFastshiftSelector,
	getFastshiftTopSupportRequests,
	FastshiftDispatchPromise,
} from '@/state';

type UseTopMigrationTickets = () => {
	pending: boolean;
	error: string | null;
	supportTickets: {
		supportTicketId: string;
		title: string;
		status: FastshiftTicketStatus;
	}[];
	total: number;
	handleRefresh: (customerDomainId: string) => FastshiftDispatchPromise<unknown> | undefined;
};

const TOP_SUPPORT_REQUESTS_COUNT = 3;

export const useTopMigrationTickets: UseTopMigrationTickets = () => {
	const dispatch = useFastshiftDispatch();
	const isOrganizationRedesignEnabled = checkFeatureFlagGate(
		featureFlags.sxpFspOrganizationRedesign,
	);

	const selectedDomainOrg = useFastshiftSelector((state) => state.selectedDomainOrg);
	const requestDomainOrg = isOrganizationRedesignEnabled ? null : selectedDomainOrg;

	const { data: customerDomainData } = useFastshiftSelector((state) => state.customerDomain);

	const stargateClient = useBxpWebStargateClient();

	const {
		pending,
		error,
		data: topSupportRequestsData,
		lastFetchParams,
	} = useFastshiftSelector((state) => state.topSupportRequests);

	const supportTicketQueryParams = useMemo(() => {
		return requestDomainOrg ? getOrgQueryParam(requestDomainOrg) : {};
	}, [requestDomainOrg]);

	const getTopSupportRequests = useCallback(
		(customerDomainId: string) => {
			if (!isOrganizationRedesignEnabled && !requestDomainOrg) {
				return;
			}

			return dispatch(
				getFastshiftTopSupportRequests({
					stargateClient,
					domainId: customerDomainId,
					count: TOP_SUPPORT_REQUESTS_COUNT,
					queryParams: supportTicketQueryParams,
				}),
			);
		},
		[
			dispatch,
			isOrganizationRedesignEnabled,
			requestDomainOrg,
			stargateClient,
			supportTicketQueryParams,
		],
	);

	useEffect(() => {
		let topSupportRequestsPromise: FastshiftDispatchPromise<unknown> | null = null;

		if (customerDomainData && (isOrganizationRedesignEnabled || requestDomainOrg)) {
			// Check if we need to fetch based on lastFetchParams
			const needsFetch =
				!lastFetchParams ||
				lastFetchParams.customerDomainId !== customerDomainData.customerDomainId ||
				lastFetchParams.queryParams.orgId !== supportTicketQueryParams.orgId ||
				lastFetchParams.queryParams.permissionLevel !== supportTicketQueryParams.permissionLevel;

			// Skip fetch if data already cached for these params
			if (!needsFetch) {
				return;
			}

			topSupportRequestsPromise =
				getTopSupportRequests(customerDomainData.customerDomainId) ?? null;
		}

		return () => {
			if (topSupportRequestsPromise) {
				topSupportRequestsPromise.abort();
			}
		};
	}, [
		dispatch,
		stargateClient,
		customerDomainData,
		isOrganizationRedesignEnabled,
		requestDomainOrg,
		supportTicketQueryParams,
		lastFetchParams,
		getTopSupportRequests,
	]);

	return {
		pending,
		error,
		...(topSupportRequestsData ? topSupportRequestsData : { supportTickets: [], total: 0 }),
		handleRefresh: getTopSupportRequests,
	};
};
